import type { WsMessage } from "@/api/websocket";
import { getDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { longDate } from "@/lib/long-date";
import { hasDeferredSystemNotice } from "@/lib/onboarding-system-notices";
import { showPlanLimitToast, showUpgradeToast } from "@/lib/upgrade-toast";
import { translate } from "@/i18n";
import { libPlanMessages } from "@/i18n/messages/lib-plan";

const announcedOnscreenPeriods = new Set<string>();

/** One toast per account per allowance period; later refusals stay quiet. */
function announceOnscreenLimit(resetAt: string): void {
  const account = getDesktopChatAuthScope()?.accountId;
  if (!account) return;
  const key = `denker.onscreen-limit.${account}.${resetAt}`;
  if (announcedOnscreenPeriods.has(key)) return;
  try {
    if (localStorage.getItem(key)) return;
  } catch {
    /* Storage may be unavailable. */
  }
  announcedOnscreenPeriods.add(key);
  showUpgradeToast({
    id: key,
    title: translate(libPlanMessages, "onscreenUsedUpTitle"),
    description: translate(libPlanMessages, "onscreenUsedUpDescription", {
      count: 25,
      date: longDate(resetAt, new Date(), "UTC"),
    }),
  });
  // A notice held back during onboarding is flagged when it is presented.
  if (hasDeferredSystemNotice(key)) return;
  try {
    localStorage.setItem(key, "1");
  } catch {
    /* In-memory deduplication remains available. */
  }
}

/** MCP requests bypass browser API middleware; the server notifies their owner. */
export function applyBillingLimitEvent(message: WsMessage): boolean {
  if (message.type === "billing.subscription_changed") {
    window.dispatchEvent(new Event("billing-status-changed"));
    return true;
  }
  if (message.type === "billing.plan_limit_exceeded") {
    const data = message.data;
    if (
      data?.code === "plan_limit_exceeded" &&
      typeof data.message === "string" &&
      data.message.trim() &&
      getWindowRoleSync() === "owner"
    ) {
      showPlanLimitToast(data.message);
      window.dispatchEvent(new Event("billing-status-changed"));
    }
    return true;
  }
  if (message.type !== "billing.onscreen_limit_exceeded") return false;
  const data = message.data;
  if (
    data?.code !== "plan_limit_exceeded" ||
    data.resource !== "desktop_onscreen" ||
    getWindowRoleSync() !== "owner"
  )
    return true;

  window.dispatchEvent(new Event("billing-status-changed"));
  const resetAt = data.onscreen_calls_reset_at;
  if (typeof resetAt === "string" && Number.isFinite(Date.parse(resetAt)))
    announceOnscreenLimit(resetAt);
  return true;
}
